import {
  Autocomplete,
  Box,
  Checkbox,
  SxProps,
  TextField,
  Tooltip,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useFormStyles } from "../../hooks/useFormStyles";


const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface TooltipOnOverflowProps {
  title: string;
  children: React.ReactNode;
}

const TooltipOnOverflow = (props: TooltipOnOverflowProps) => {
  const { title, children } = props;
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    const el = e.currentTarget;
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  };

  return (
    <Tooltip title={title} disableHoverListener={!isOverflowing}>
      <Box
        onMouseEnter={handleMouseEnter}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

interface FormMultiAutocompleteProps {
  name: string;
  label: string;
  options: { id: number; name: string; code?: string }[];
  isEditing: boolean;
  isSaving: boolean;
  disabled?: boolean;
  formStyles: SxProps;
}

function FormMultiAutocomplete({
  name,
  label,
  options,
  isEditing,
  isSaving,
  disabled = false,
}: Readonly<FormMultiAutocompleteProps>) {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const formStyles = useFormStyles(isEditing);

  const optionsMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string; code?: string }>();
    options.forEach(opt => map.set(opt.id, opt));
    return map;
  }, [options]);

  const getLabel = (option: { id: number; name: string; code?: string }) =>
    option.code ? `(${option.code}) ${option.name}` : option.name;

  return (
    <Controller
      name={name as any}
      control={control}
      render={({ field, fieldState }) => {
        const selectedOptions = (field.value || []).map((id: number) => optionsMap.get(id)).filter(Boolean) as { id: number; name: string; code?: string }[];

        return (
          <Autocomplete
            multiple
            fullWidth
            options={options}
            value={selectedOptions}
            getOptionLabel={getLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, newValue) => field.onChange(newValue ? newValue.map(v => v.id) : [])}
            disabled={!isEditing || isSaving || disabled || options.length === 0}
            autoHighlight
            disableCloseOnSelect
            noOptionsText={t("generic.no-options")}
            renderOption={(props, option, { selected }) => {
              const label = getLabel(option);
              return (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  <TooltipOnOverflow title={label}>{label}</TooltipOnOverflow>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant={isEditing ? "outlined" : "standard"}
                sx={{ ...formStyles }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        );
      }}
    />
  );
}

export default FormMultiAutocomplete;
